/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
  CloseCircleFilled,
  QuestionCircleFilled,
  CheckCircleFilled,
  MedicineBoxFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import BaseModal from '../../ts-components/BaseModal'
import { ATTENDANCE_STATUS_COPY } from '../utils/statusCopy'

export interface AttendanceHelpModalProps {
  open: boolean
  onClose: () => void
}

interface StatusItem {
  key: string
  Icon: React.ComponentType<{ style?: React.CSSProperties }>
  color: string
}

// Copy comes from the shared ATTENDANCE_STATUS_COPY map so the row tooltips and
// this modal never drift.
const STATUSES: StatusItem[] = [
  { key: 'not_attending', Icon: CloseCircleFilled, color: colors.FIERY_RED },
  {
    key: 'maybe_attending',
    Icon: QuestionCircleFilled,
    color: colors.AMERICAN_YELLOW,
  },
  { key: 'attending', Icon: CheckCircleFilled, color: colors.GREEN_GOBLIN },
  { key: 'injury', Icon: MedicineBoxFilled, color: colors.FIERY_RED },
  {
    key: 'suspended',
    Icon: ExclamationCircleFilled,
    color: colors.FIERY_RED,
  },
]

const subtitleStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 16px;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid ${colors.SECONDARY_200};
`

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 24,
  color,
  flexShrink: 0,
})

const statusTitleStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  margin: 0;
`

const statusDescStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 12px;
  line-height: 17px;
  margin: 2px 0 0;
`

const AttendanceHelpModal: React.FC<AttendanceHelpModalProps> = ({
  open,
  onClose,
}) => (
  <BaseModal
    isOpen={open}
    onClose={onClose}
    title='Attendance Status'
    desktopWidth='480px'
    actions={<span />}
    elevated
  >
    <p css={subtitleStyle}>
      These are the statuses set by the player attendance feature. You can
      review this information at anytime by pressing the info icon.
    </p>
    <div css={listStyle}>
      {STATUSES.map(({ key, Icon, color }) => (
        <div key={key} css={rowStyle}>
          <Icon style={iconStyle(color)} />
          <div>
            <p css={statusTitleStyle}>{ATTENDANCE_STATUS_COPY[key].title}</p>
            <p css={statusDescStyle}>
              {ATTENDANCE_STATUS_COPY[key].description}
            </p>
          </div>
        </div>
      ))}
    </div>
  </BaseModal>
)

export default AttendanceHelpModal
